import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * Generate a recipe from a free-text prompt using the AI text endpoint.
 * Emits `onGenerated(recipeId)` when a new recipe is saved so the parent can
 * reload lists and trigger downstream actions (tag-suggestion, etc).
 */
export default function AIRecipeDialog({ open, onOpenChange, servings, onGenerated }) {
  const { activeBoat, loadActiveBoat } = useAuth();
  const [prompt, setPrompt] = useState("");
  const [mealType, setMealType] = useState("dinner");
  const [loading, setLoading] = useState(false);

  const maybeSuggestNewCategory = (recipe) => {
    if (!recipe?.is_baking) return;
    const cat = (recipe.category || "").trim();
    if (!cat) return;
    const existing = activeBoat?.baking_categories || [];
    if (existing.some((c) => c.toLowerCase() === cat.toLowerCase())) return;
    toast.info(`AI brukte kategorien '${cat}'`, {
      description: "Vil du legge den til som en fast bakst-kategori?",
      duration: 8000,
      action: {
        label: "Ja, legg til",
        onClick: async () => {
          if (!activeBoat?.id) return;
          try {
            await api.updateBoatBakingCategories(activeBoat.id, [...existing, cat]);
            if (loadActiveBoat) loadActiveBoat();
            toast.success(`'${cat}' lagt til som bakst-kategori`);
          } catch (err) {
            toast.error(err.response?.data?.detail || "Kunne ikke lagre kategori");
          }
        },
      },
    });
  };

  const generate = async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    try {
      const res = await api.generateRecipe({
        prompt,
        meal_type: mealType,
        servings,
        is_baking: mealType === "baking",
      });
      toast.success("AI-oppskrift generert!");
      maybeSuggestNewCategory(res.data);
      onOpenChange(false);
      setPrompt("");
      onGenerated?.(res.data?.id);
    } catch {
      toast.error("Kunne ikke generere oppskrift");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[92vh] flex flex-col p-0" data-testid="ai-recipe-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-stone-100">
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            <Sparkles className="h-5 w-5 text-orange-700" />AI Oppskrift Generator
          </DialogTitle>
          <DialogDescription className="text-xs">Beskriv kva du ønsker og AI lagar oppskrifta</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-5 py-4 min-h-0">
          <div className="space-y-4">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-1.5 block">Prompt</label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="F.eks. ein enkel og sunn middag med kylling og grønnsaker…"
                className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none resize-none min-h-[100px]"
                data-testid="ai-recipe-prompt"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-1.5 block">Måltidstype</label>
              <Select value={mealType} onValueChange={setMealType}>
                <SelectTrigger data-testid="ai-meal-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="breakfast">Frokost</SelectItem>
                  <SelectItem value="lunch">Lunsj</SelectItem>
                  <SelectItem value="dinner">Middag</SelectItem>
                  <SelectItem value="baking">Baking</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {loading && (
              <div className="space-y-3 py-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-4 w-2/3" />
                <p className="text-xs text-stone-400 text-center mt-2">Genererer oppskrift med AI…</p>
              </div>
            )}
          </div>
        </div>
        <div className="border-t border-stone-100 px-5 py-3 bg-white">
          <button
            onClick={generate}
            disabled={loading || !prompt.trim()}
            className="w-full inline-flex items-center justify-center rounded-md bg-orange-700 px-4 py-3 text-sm font-semibold text-white hover:bg-orange-800 transition-colors gap-2 disabled:opacity-50"
            data-testid="generate-recipe-btn"
          >
            <Sparkles className="h-4 w-4" />
            {loading ? "Genererer…" : "Generer oppskrift"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
